import {useEffect, useState} from 'react';

import {merchantTransactionService} from '@/services/merchants/transactionService';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import {
  getPoolDepositDuplicateWarning,
  type PoolDepositDuplicateWarning,
} from './poolDepositDuplicate';

/**
 * Approve / reject actions for an entity-submitted pool deposit.
 *
 * Approve sends the amount only when the operator changed it, so the default
 * one-click path credits exactly what the entity declared.
 */
export const usePoolDepositReview = (
  transactionId: string,
  submittedAmount: number,
) => {
  const [amount, setAmount] = useState<string>(String(submittedAmount ?? ''));
  const [reason, setReason] = useState('');
  const [resubmissionRequired, setResubmissionRequired] = useState(true);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<
    PoolDepositDuplicateWarning | undefined
  >();
  // Cancel only hides the warning; the duplicate keeps blocking Approve (#7356).
  const [isDuplicateDismissed, setIsDuplicateDismissed] = useState(false);

  // Re-seed the editable amount when the submitted amount changes. The panel is
  // keyed by transaction id, so another transaction remounts it instead.
  useEffect(() => {
    setAmount(String(submittedAmount ?? ''));
  }, [transactionId, submittedAmount]);

  const parsedAmount = Number(amount);
  const isAmountAdjusted =
    Number.isFinite(parsedAmount) &&
    parsedAmount > 0 &&
    parsedAmount !== Number(submittedAmount);
  // Whether the approve request as filled in is well-formed. Deliberately
  // does not factor in duplicateWarning: whether the button should be
  // disabled while a duplicate is flagged is a UI-enablement concern for the
  // caller to combine at the render call site (see PoolDepositReviewPanel),
  // matching how usePoolDepositSubmit.isValid stays a pure form-validity
  // check on the business-web side of this same fix.
  const canApprove =
    !isLoading && Number.isFinite(parsedAmount) && parsedAmount > 0;
  const canReject = !isLoading && reason.trim().length > 0;

  const refresh = () => {
    setTimeout(() => window.location.reload(), 1000);
  };

  // On success we intentionally leave isLoading true: `refresh` reloads the page
  // in ~1s, and clearing it would re-enable the buttons in that window, letting
  // a double-click fire a second approve/reject the server then 409s (with a
  // confusing error toast on top of the success). Only the error path re-enables.
  const handleApprove = async (acknowledgeDuplicate: boolean = false) => {
    setIsLoading(true);
    try {
      await merchantTransactionService.approvePoolDeposit(transactionId, {
        ...(isAmountAdjusted ? {amount: parsedAmount} : {}),
        ...(acknowledgeDuplicate ? {acknowledgeDuplicate: true} : {}),
      });
      setDuplicateWarning(undefined);
      showSuccessMessage('Pool deposit approved and credited');
      refresh();
    } catch (e) {
      // A matching pool deposit already exists (already credited or
      // mid-settlement): surface it as a confirmation prompt rather than a
      // plain error, mirroring the business-portal submit flow. Only on the
      // first attempt; a retry that still comes back duplicate falls through
      // to a plain error toast.
      const duplicate = acknowledgeDuplicate
        ? undefined
        : getPoolDepositDuplicateWarning(e);
      if (duplicate) {
        setDuplicateWarning(duplicate);
        setIsLoading(false);
        return;
      }
      showErrorMessage(
        `Error approving deposit: ${getErrorMessage(e, String(e))}`,
      );
      setIsLoading(false);
    }
  };

  const confirmApproveDuplicate = () => handleApprove(true);
  const dismissDuplicateWarning = () => setIsDuplicateDismissed(true);

  const handleReject = async () => {
    setIsLoading(true);
    try {
      await merchantTransactionService.rejectPoolDeposit(transactionId, {
        reason: reason.trim(),
        resubmissionRequired,
      });
      showSuccessMessage('Pool deposit rejected');
      refresh();
    } catch (e) {
      showErrorMessage(
        `Error rejecting deposit: ${getErrorMessage(e, String(e))}`,
      );
      setIsLoading(false);
    }
  };

  return {
    amount,
    setAmount,
    isAmountAdjusted,
    reason,
    setReason,
    resubmissionRequired,
    setResubmissionRequired,
    isRejecting,
    setIsRejecting,
    isLoading,
    canApprove,
    canReject,
    handleApprove,
    handleReject,
    duplicateWarning,
    isDuplicateDismissed,
    confirmApproveDuplicate,
    dismissDuplicateWarning,
  };
};
